import { runStatsRecordFelDefeat } from '@helpers/run-stats/run-stats';
import { getEntry } from '@helpers/content/content';
import {
  analyticsSafeSegment,
  analyticsSendDesignEvent,
} from '@helpers/engine/analytics';
import { ledgerHas } from '@helpers/engine/ledger';
import { recomputeGlobalEffectSums } from '@helpers/hero/global-effect-state';
import { collectiblesState, updateGamestate } from '@helpers/state-game';
import type {
  CollectibleContent,
  CollectibleId,
  GameState,
  GameStateCollectibles,
} from '@interfaces';
import { taskEventCollectibleGained } from '@helpers/task/task-events';

export function getCollectibleQuantity(collectibleId: CollectibleId): number {
  return collectiblesState()[collectibleId]?.quantity ?? 0;
}

export function isCollectibleDiscovered(collectibleId: CollectibleId): boolean {
  return ledgerHas(collectiblesState(), collectibleId);
}

export function discoveredCollectibleCount(): number {
  return Object.values(collectiblesState()).filter((entry) => !!entry?.foundAt)
    .length;
}

// Shared raw mutator - callers already inside their own `updateGamestate`/tick
// draft (loot, caravan trades) call this directly instead of `collectiblesAdd`.
export function applyCollectibleGrant(
  state: GameState,
  collectibleId: CollectibleId,
  quantity: number,
): void {
  const existing = state.collectibles[collectibleId];
  state.collectibles[collectibleId] = {
    quantity: (existing?.quantity ?? 0) + quantity,
    foundAt: existing?.foundAt ?? Date.now(),
  };
  recomputeGlobalEffectSums(state);
}

// Only clearing Forgetter's Fane awards it, so owning it means Fel has fallen at least once.
const FINAL_COLLECTIBLE_NAME = 'Memory of the Forgetter';

export function finalCollectibleId(): CollectibleId | undefined {
  return getEntry<CollectibleContent>(FINAL_COLLECTIBLE_NAME)?.id;
}

export function collectiblesAdd(
  collectibleId: CollectibleId,
  quantity = 1,
): void {
  if (quantity <= 0) return;

  const alreadyDiscovered = isCollectibleDiscovered(collectibleId);

  updateGamestate((state) => {
    applyCollectibleGrant(state, collectibleId, quantity);
    // Only the first discovery is the defeat; refights grant it again.
    if (!alreadyDiscovered && collectibleId === finalCollectibleId()) {
      runStatsRecordFelDefeat(state);
    }
    return state;
  });
  void taskEventCollectibleGained(collectibleId);

  if (!alreadyDiscovered) {
    const collectibleName = getEntry<CollectibleContent>(collectibleId)?.name;

    if (collectibleName) {
      analyticsSendDesignEvent(
        `Progress:Museum:Unlock:${analyticsSafeSegment(collectibleName)}`,
      );
    }
  }
}

const FOUNDING_STONE_NAME = 'Founding Stone';

// Every player is guaranteed to own the Founding Stone - it isn't dropped by
// any encounter, so new/migrated saves are granted one if they don't already
// have it.
export function grantFoundingStoneIfMissing(
  collectibles: GameStateCollectibles,
): GameStateCollectibles {
  const foundingStone = getEntry<CollectibleContent>(FOUNDING_STONE_NAME);
  if (!foundingStone) return collectibles;

  if (collectibles[foundingStone.id]) return collectibles;

  return {
    ...collectibles,
    [foundingStone.id]: { quantity: 1, foundAt: Date.now() },
  };
}
